/**
 * Hotels service for interacting with the room worker API
 */

import { room } from '@/config/api';
import { getCurrentLocation } from '@/services/weatherService';

/**
 * Get nearby hotels based on current location
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @returns {Promise<Object>} - Hotels data
 * @throws {Error} - If the request fails or coordinates are invalid
 */
export async function getNearbyHotels(lat, lon) {
  if (!lat || !lon) {
    throw new Error('Latitude and longitude are required');
  }

  if (typeof lat !== 'number' || typeof lon !== 'number' || 
      isNaN(lat) || isNaN(lon) ||
      lat < -90 || lat > 90 || lon < -180 || lon > 180) {
    throw new Error('Invalid coordinates provided');
  }

  try {
    // Call the room worker API
    const response = await fetch(`${room}/hotels?lat=${lat}&lon=${lon}`);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Failed to fetch nearby hotels: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while fetching nearby hotels');
  }
}

/**
 * Get nearby hotels using current location
 * @returns {Promise<{hotels: Array, coordinates: {lat: number, lon: number}}>}
 */
export async function getNearbyPlaces() {
  // Get current location
  const coordinates = await getCurrentLocation();

  // Fetch hotels
  const hotelsResponse = await getNearbyHotels(coordinates.lat, coordinates.lon);

  return {
    hotels: hotelsResponse.hotels || [],
    coordinates
  };
}
